import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { DBEquipmentItem } from "@/lib/db-types";

export type EquipmentInput = Pick<
  DBEquipmentItem,
  | "name"
  | "category_id"
  | "department_id"
  | "quantity"
  | "serial_numbers"
  | "status"
  | "bookable"
  | "insured_until"
  | "purchase_date"
  | "rental_price_day"
  | "parts"
  | "comment"
> & { insurance_value: number | null; purchase_price: number | null };

export type EquipmentPatch = Partial<EquipmentInput> & {
  insurance_declined?: boolean;
  deleted_at?: string | null;
};

/**
 * Registrer utstyr. created_by er den ekte innloggede brukeren (RLS krever
 * created_by = current_user_id()), også når admin opptrer som en annen.
 */
export function useCreateEquipment() {
  const qc = useQueryClient();
  const { selfUser } = useImpersonation();
  return useMutation({
    mutationFn: async (input: EquipmentInput): Promise<string> => {
      if (!selfUser) throw new Error("Fant ikke innlogget bruker");
      const { data, error } = await supabase
        .from("equipment_items")
        .insert({ ...input, source: "Revehiet", created_by: selfUser.id })
        .select("id")
        .single();
      if (error) throw new Error(`Kunne ikke registrere utstyret: ${error.message}`);
      return (data as { id: string }).id;
    },
    onSuccess: async (id) => {
      await invalidateFor(qc, "equipment_items", { equipmentId: id });
    },
  });
}

/** Endre ett eller flere utstyr med samme endring (f.eks. «Forny valgte»). */
export function useUpdateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, patch }: { ids: string[]; patch: EquipmentPatch }) => {
      if (ids.length === 0) return;
      const { data, error } = await supabase.from("equipment_items").update(patch).in("id", ids).select("id");
      if (error) throw new Error(`Kunne ikke lagre: ${error.message}`);
      if ((data ?? []).length !== ids.length) throw new Error("Du har ikke tilgang til å endre utstyret");
    },
    onSuccess: async () => {
      // Uten context invalideres alle equipment-detail/-history-nøkler.
      await invalidateFor(qc, "equipment_items");
    },
  });
}
